import { useState } from "react";
import { Link } from "react-router";

import { cn } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { ROUTES } from "@/navigation/constants/routes";
import {
  JEWEL_CASE_THICKNESS_MILLIMETERS,
  SPINE_ARTWORK_HEIGHT_MILLIMETERS,
  SPINE_ARTWORK_WIDTH_MILLIMETERS,
} from "@/shelf/constants/shelf-geometry";
import {
  BoardBoxElement,
  BoardFineBoxElement,
  BoardRotatedGroup,
} from "@/shelf/components/shelf-board/board-surface";

import type { BoardBox, ShelfSpine } from "@/shelf/types/types";
import type { ReactNode } from "react";

/**
 * What is printed on the spine: the Back artwork's own flap, or the project's
 * name when there is no artwork to print.
 *
 * The name is the fallback rather than the design. A rack of names is what a
 * shelf of unlabelled discs would look like anyway, and it is honest about a
 * project whose Back page has never been drawn — but wherever there is artwork,
 * the artwork is what stands there.
 *
 * The type is sized in the same millimeters everything else on the board is, so
 * it grows and shrinks with the disc rather than staying the same size while the
 * case around it changes.
 */
const ShelfSpineFace = ({ spine }: { spine: ShelfSpine }) => {
  const { t } = useTranslation();
  // A spine the browser cannot draw is not a spine. The head names a picture by
  // digest, and an object released or never uploaded leaves an empty box where
  // the album should be - which is worse than the fallback, because a disc with
  // nothing on it is the one thing that cannot be identified from the board.
  const [hasImageFailed, setImageFailed] = useState(false);
  if (spine.status === "shelved" && spine.spineImageUrl && !hasImageFailed) {
    return (
      <img
        // The disc already names itself through its own accessible name, and a
        // screen reader announcing the title twice is noise.
        alt=""
        src={spine.spineImageUrl}
        className="size-full object-cover"
        draggable={false}
        onError={() => setImageFailed(true)}
      />
    );
  }
  // Laid out flat and then turned, rather than set in a vertical writing mode.
  // The strip is already inside a scaled transform and often inside a turned
  // rack, and a flex box whose writing mode swaps its own axes on top of that
  // collapses to nothing — the case is drawn and the name inside it is not.
  const label: BoardBox = {
    xMillimeters: (SPINE_ARTWORK_WIDTH_MILLIMETERS - SPINE_ARTWORK_HEIGHT_MILLIMETERS) / 2,
    yMillimeters: (SPINE_ARTWORK_HEIGHT_MILLIMETERS - SPINE_ARTWORK_WIDTH_MILLIMETERS) / 2,
    widthMillimeters: SPINE_ARTWORK_HEIGHT_MILLIMETERS,
    heightMillimeters: SPINE_ARTWORK_WIDTH_MILLIMETERS,
  };
  // The name is set in millimetres like everything else on the board, so it is
  // drawn back at millimetre scale inside the finer box the strip is placed in.
  return (
    <span className="absolute left-0 top-0 block size-[10%] origin-top-left scale-[10] overflow-hidden bg-card">
      <BoardRotatedGroup
        box={label}
        // Read top to bottom, the way spine artwork is printed, so the name
        // reads left to right on a rack at rest the way the artwork does.
        rotationDegrees={90}
        pivotXMillimeters={label.widthMillimeters / 2}
        pivotYMillimeters={label.heightMillimeters / 2}
      >
        <span
          className={cn(
            "flex size-full items-center justify-center px-[2px]",
            spine.status === "absent" ? "text-muted-foreground" : "text-foreground",
          )}
        >
          <span className="truncate text-[4.2px] font-semibold leading-none tracking-tight">
            {spine.status === "absent" ? t("shelf.spine.unknownName") : spine.title}
          </span>
        </span>
      </BoardRotatedGroup>
    </span>
  );
};

const caseClassName = cn(
  "relative block size-full outline-none",
  // A continuous run of cases separated by one quiet seam, as the desktop draws
  // them. The seam is a shadow, not a border: a border takes room inside the
  // case and set the printed strip off its centre.
  "bg-(--shelf-case) shadow-[inset_0.35px_0_0_rgba(255,255,255,0.12)]",
);

/**
 * One disc standing in a slot: a jewel case seen edge on, with the printed
 * spine of its Back artwork inside it.
 *
 * The case and the print are drawn as two things because they are two things —
 * a case is 10.4mm of plastic and the artwork inside it is 6.5mm of paper, and
 * a rack that drew the art edge to edge would be a row of coloured bars rather
 * than a shelf of CDs. The plastic is what keeps one disc distinguishable from
 * the next when the whole board is zoomed out.
 *
 * A disc naming a project the Cloud Library no longer holds still stands, and
 * is deliberately **not** a link: there is nothing to open. A control that can
 * never apply is absent rather than dead, so the case is drawn as plain text
 * that says so, rather than as a link to a page that would 404.
 */
export const ShelfSpineItem = ({ box, spine }: { box: BoardBox; spine: ShelfSpine }) => {
  const { t } = useTranslation();
  const artworkBox: BoardBox = {
    xMillimeters: (JEWEL_CASE_THICKNESS_MILLIMETERS - SPINE_ARTWORK_WIDTH_MILLIMETERS) / 2,
    yMillimeters: (box.heightMillimeters - SPINE_ARTWORK_HEIGHT_MILLIMETERS) / 2,
    widthMillimeters: SPINE_ARTWORK_WIDTH_MILLIMETERS,
    heightMillimeters: SPINE_ARTWORK_HEIGHT_MILLIMETERS,
  };

  // Placed exactly: laid out in whole millimetres, the 6.5 mm strip was
  // rounded to 6 and sat off centre in its 10.4 mm case.
  const face: ReactNode = (
    <BoardFineBoxElement box={artworkBox} className="overflow-hidden rounded-[3px]">
      <ShelfSpineFace spine={spine} />
    </BoardFineBoxElement>
  );

  return (
    <BoardBoxElement box={box}>
      {spine.status === "absent" ? (
        <span
          role="img"
          aria-label={t("shelf.spine.unknown")}
          className={cn(caseClassName, "opacity-70")}
        >
          {face}
        </span>
      ) : (
        <Link
          to={`${ROUTES.catalog}/${encodeURIComponent(spine.desktopProjectId)}`}
          aria-label={t("shelf.spine.open", { title: spine.title })}
          // A drag that begins on a disc pans the board. The browser's own link
          // drag would take the pointer and cancel it, so the board did not
          // move whenever a press landed on a rack.
          draggable={false}
          className={cn(
            caseClassName,
            // The whole disc is the target, because at the far end of the zoom
            // range a disc is a few pixels wide and anything smaller could not
            // be hit. A disc is reachable by keyboard, so it has to be able to
            // say when it has been reached.
            "transition-[box-shadow,filter] hover:z-10 hover:brightness-110 hover:ring-[0.6px] hover:ring-primary",
            "focus-visible:z-10 focus-visible:ring-[0.8px] focus-visible:ring-inset focus-visible:ring-primary",
          )}
        >
          {face}
        </Link>
      )}
    </BoardBoxElement>
  );
};
